import { lazy } from 'react';
import { ExecutionRunsPage } from '@/features/executionRuns/pages/ExecutionRunsPage';
import { FeaturesPage } from '@/features/features/routes';
import { JobsPage } from '@/features/jobs/routes';
import { PipelinesPage } from '@/features/pipelines/pages/PipelinesPage';
import { StagesPage } from '@/features/pipelines/pages/StagesPage';
import { ReleasesPage } from '@/features/releases/routes';
import { SuitesPage } from '@/features/suites/routes';
import { TestsPage } from '@/features/tests/routes';

const LandingPage = lazy(async () => {
  const module = await import('../../pages/LandingPage');
  return { default: module.LandingPage };
});

const PreviewPage = lazy(async () => {
  const module = await import('../../pages/PreviewPage');
  return { default: module.PreviewPage };
});

const PlanPage = lazy(async () => {
  const module = await import('../../pages/PlanPage');
  return { default: module.PlanPage };
});

const PrivacyRequestsPage = lazy(async () => {
  const module = await import('../../pages/PrivacyRequestsPage');
  return { default: module.PrivacyRequestsPage };
});

const ReportsPage = lazy(async () => {
  const module = await import('../../pages/ReportsPage');
  return { default: module.ReportsPage };
});

const ResetPasswordConfirmPage = lazy(async () => {
  const module = await import('../../pages/ResetPasswordConfirmPage');
  return { default: module.ResetPasswordConfirmPage };
});

const ResetPasswordPage = lazy(async () => {
  const module = await import('../../pages/ResetPasswordPage');
  return { default: module.ResetPasswordPage };
});

const SessionExpired = lazy(async () => {
  const module = await import('../../pages/SessionExpired');
  return { default: module.SessionExpired };
});

const SuperuserPage = lazy(async () => {
  const module = await import('../../pages/SuperuserPage');
  return { default: module.SuperuserPage };
});

const UserSettingsPage = lazy(async () => {
  const module = await import('../../pages/UserSettingsPage');
  return { default: module.UserSettingsPage };
});

const VerifyEmailPage = lazy(async () => {
  const module = await import('../../pages/VerifyEmailPage');
  return { default: module.VerifyEmailPage };
});

const ExecutionRunsTableDebugPage = lazy(async () => {
  const module = await import('@/features/executionRuns/pages/ExecutionRunsTableDebugPage');
  return { default: module.ExecutionRunsTableDebugPage };
});

const ExecutionRunsTablePanelDebugPage = lazy(async () => {
  const module = await import('@/features/executionRuns/pages/ExecutionRunsTablePanelDebugPage');
  return { default: module.ExecutionRunsTablePanelDebugPage };
});

const AuthDebugIndexPage = lazy(async () => {
  const module = await import('@/features/authDebug/pages/AuthDebugIndexPage');
  return { default: module.AuthDebugIndexPage };
});

const AuthDebugScreenPage = lazy(async () => {
  const module = await import('@/features/authDebug/pages/AuthDebugScreenPage');
  return { default: module.AuthDebugScreenPage };
});

const BuildDetailPage = lazy(async () => {
  const module = await import('@/features/builds/routes');
  return { default: module.BuildDetailPage };
});

const JobRunDetailPage = lazy(async () => {
  const module = await import('@/features/builds/routes');
  return { default: module.JobRunDetailPage };
});

const ExecutionRunDetailPage = lazy(async () => {
  const module = await import('@/features/executionRuns/pages/ExecutionRunDetailPage');
  return { default: module.ExecutionRunDetailPage };
});

const FeatureDetailPage = lazy(async () => {
  const module = await import('@/features/features/routes');
  return { default: module.FeatureDetailPage };
});

const OnboardingPage = lazy(async () => {
  const module = await import('@/features/onboarding/pages/OnboardingPage');
  return { default: module.OnboardingPage };
});

const OnboardingDebugIndexPage = lazy(async () => {
  const module = await import('@/features/onboardingDebug/pages/OnboardingDebugIndexPage');
  return { default: module.OnboardingDebugIndexPage };
});

const OnboardingDebugScreenPage = lazy(async () => {
  const module = await import('@/features/onboardingDebug/pages/OnboardingDebugScreenPage');
  return { default: module.OnboardingDebugScreenPage };
});

const OrganisationSettingsPage = lazy(async () => {
  const module = await import('@/features/organisations/pages/OrganisationSettingsPage');
  return { default: module.OrganisationSettingsPage };
});

const ProjectDetailPage = lazy(async () => {
  const module = await import('@/features/projects/routes');
  return { default: module.ProjectDetailPage };
});

const PipelineDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/PipelineDetailPage');
  return { default: module.PipelineDetailPage };
});

const StageDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/StageDetailPage');
  return { default: module.StageDetailPage };
});

const StageExecutionDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/StageExecutionDetailPage');
  return { default: module.StageExecutionDetailPage };
});

const JobDetailPage = lazy(async () => {
  const module = await import('@/features/jobs/routes');
  return { default: module.JobDetailPage };
});

const ReleaseDetailPage = lazy(async () => {
  const module = await import('@/features/releases/routes');
  return { default: module.ReleaseDetailPage };
});

const SignInPage = lazy(async () => {
  const module = await import('../../pages/SignInPage');
  return { default: module.SignInPage };
});

const SignUpPage = lazy(async () => {
  const module = await import('../../pages/SignUpPage');
  return { default: module.SignUpPage };
});

const AuthCallbackPage = lazy(async () => {
  const module = await import('../../pages/AuthCallbackPage');
  return { default: module.AuthCallbackPage };
});

const AcceptInvitationPage = lazy(async () => {
  const module = await import('../../pages/AcceptInvitationPage');
  return { default: module.AcceptInvitationPage };
});

const SuiteDetailPage = lazy(async () => {
  const module = await import('@/features/suites/routes');
  return { default: module.SuiteDetailPage };
});

const SuiteRunDetailPage = lazy(async () => {
  const module = await import('@/features/executions/routes');
  return { default: module.SuiteRunDetailPage };
});

const TestDetailPage = lazy(async () => {
  const module = await import('@/features/tests/routes');
  return { default: module.TestDetailPage };
});

const TestRunDetailPage = lazy(async () => {
  const module = await import('@/features/executions/routes');
  return { default: module.TestRunDetailPage };
});

const ResultsFileDetailPage = lazy(async () => {
  const module = await import('@/features/uploads/routes');
  return { default: module.ResultsFileDetailPage };
});

const UploadsPage = lazy(async () => {
  const module = await import('@/features/uploads/routes');
  return { default: module.UploadsPage };
});

const UploadDetailPage = lazy(async () => {
  const module = await import('@/features/uploads/routes');
  return { default: module.UploadDetailPage };
});

export {
  AcceptInvitationPage,
  AuthDebugIndexPage,
  AuthDebugScreenPage,
  AuthCallbackPage,
  BuildDetailPage,
  ExecutionRunDetailPage,
  ExecutionRunsPage,
  ExecutionRunsTableDebugPage,
  ExecutionRunsTablePanelDebugPage,
  FeatureDetailPage,
  FeaturesPage,
  JobDetailPage,
  JobRunDetailPage,
  JobsPage,
  LandingPage,
  OnboardingDebugIndexPage,
  OnboardingDebugScreenPage,
  OnboardingPage,
  OrganisationSettingsPage,
  ProjectDetailPage,
  PipelineDetailPage,
  PipelinesPage,
  PlanPage,
  PreviewPage,
  PrivacyRequestsPage,
  ReleaseDetailPage,
  ReleasesPage,
  ReportsPage,
  ResetPasswordConfirmPage,
  ResetPasswordPage,
  ResultsFileDetailPage,
  SessionExpired,
  SignInPage,
  SignUpPage,
  StageDetailPage,
  StageExecutionDetailPage,
  StagesPage,
  SuiteDetailPage,
  SuiteRunDetailPage,
  SuitesPage,
  SuperuserPage,
  TestDetailPage,
  TestRunDetailPage,
  TestsPage,
  UploadsPage,
  UploadDetailPage,
  UserSettingsPage,
  VerifyEmailPage,
};
